import { useState, useEffect } from 'react';
import './AdminPronuncia.css';

const NIVEIS = ['beginner', 'intermediate'];
const NIVEL_LABELS = { beginner: 'Iniciante', intermediate: 'Intermediário' };

const EMPTY_FORM = { palavra: '', ipa: '', nivel: 'beginner', categoria: 'General', ativo: true };

export default function AdminPronuncia() {
  const [words,       setWords]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filterNivel, setFilterNivel] = useState('');
  const [search,      setSearch]      = useState('');
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [editId,      setEditId]      = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [msg,         setMsg]         = useState(null);
  const [showForm,    setShowForm]    = useState(false);

  useEffect(() => { fetchWords(); }, []);

  async function fetchWords() {
    setLoading(true);
    try {
      const res = await fetch('/api/pronuncia/admin/palavras', { credentials: 'include' });
      setWords(await res.json());
    } catch (_) {}
    setLoading(false);
  }

  function flash(text, ok = true) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  }

  function openNew() {
    setForm(EMPTY_FORM);
    setEditId(null);
    setShowForm(true);
  }

  function openEdit(w) {
    setForm({ palavra: w.palavra, ipa: w.ipa || '', nivel: w.nivel, categoria: w.categoria, ativo: !!w.ativo });
    setEditId(w.id);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.palavra.trim()) { flash('Palavra obrigatória.', false); return; }
    setSaving(true);
    try {
      const url    = editId ? `/api/pronuncia/admin/palavras/${editId}` : '/api/pronuncia/admin/palavras';
      const method = editId ? 'PUT' : 'POST';
      const res    = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      flash(editId ? 'Palavra atualizada!' : 'Palavra criada!');
      cancelForm();
      fetchWords();
    } catch (_) {
      flash('Erro ao salvar.', false);
    }
    setSaving(false);
  }

  async function handleDelete(id, palavra) {
    if (!window.confirm(`Excluir "${palavra}"?`)) return;
    try {
      await fetch(`/api/pronuncia/admin/palavras/${id}`, { method: 'DELETE', credentials: 'include' });
      flash('Excluída!');
      fetchWords();
    } catch (_) { flash('Erro ao excluir.', false); }
  }

  const filtered = words.filter(w => {
    const matchN = !filterNivel || w.nivel === filterNivel;
    const matchS = !search || w.palavra.includes(search.toLowerCase()) || (w.categoria || '').toLowerCase().includes(search.toLowerCase());
    return matchN && matchS;
  });

  const categories = [...new Set(words.map(w => w.categoria).filter(Boolean))].sort();

  return (
    <div className="ap-page">
      <div className="ap-header">
        <div>
          <h1><i className="fas fa-microphone-alt" /> Palavras de Pronúncia</h1>
          <p>{words.length} palavras cadastradas</p>
        </div>
        <button className="ap-btn-primary" onClick={openNew}>
          <i className="fas fa-plus" /> Nova Palavra
        </button>
      </div>

      {msg && (
        <div className={`ap-msg${msg.ok ? '' : ' ap-msg-err'}`}>
          <i className={`fas fa-${msg.ok ? 'check-circle' : 'exclamation-circle'}`} /> {msg.text}
        </div>
      )}

      {/* ── Form ── */}
      {showForm && (
        <form className="ap-form" onSubmit={handleSubmit}>
          <h2>{editId ? 'Editar Palavra' : 'Nova Palavra'}</h2>
          <div className="ap-form-grid">
            <div className="ap-field">
              <label>Palavra *</label>
              <input value={form.palavra} onChange={e => setForm(f => ({ ...f, palavra: e.target.value }))}
                     placeholder="ex: comfortable" required />
            </div>
            <div className="ap-field">
              <label>IPA (fonética)</label>
              <input value={form.ipa} onChange={e => setForm(f => ({ ...f, ipa: e.target.value }))}
                     placeholder="ex: /ˈkʌmftəbəl/" />
            </div>
            <div className="ap-field">
              <label>Nível</label>
              <select value={form.nivel} onChange={e => setForm(f => ({ ...f, nivel: e.target.value }))}>
                {NIVEIS.map(n => <option key={n} value={n}>{NIVEL_LABELS[n]}</option>)}
              </select>
            </div>
            <div className="ap-field">
              <label>Categoria</label>
              <input value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}
                     placeholder="ex: Food, Adjectives…" list="ap-cats" />
              <datalist id="ap-cats">
                {categories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="ap-field ap-field-check">
              <label>
                <input type="checkbox" checked={form.ativo}
                       onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))} />
                Ativo
              </label>
            </div>
          </div>
          <div className="ap-form-actions">
            <button type="submit" className="ap-btn-primary" disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Salvando…</> : <><i className="fas fa-save" /> Salvar</>}
            </button>
            <button type="button" className="ap-btn-cancel" onClick={cancelForm}>Cancelar</button>
          </div>
        </form>
      )}

      {/* ── Filters ── */}
      <div className="ap-filters">
        <input placeholder="Buscar palavra ou categoria…"
               value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}>
          <option value="">Todos os níveis</option>
          {NIVEIS.map(n => <option key={n} value={n}>{NIVEL_LABELS[n]}</option>)}
        </select>
        <span className="ap-count">{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="ap-loading"><i className="fas fa-spinner fa-spin" /> Carregando…</div>
      ) : (
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead>
              <tr>
                <th>Palavra</th>
                <th>IPA</th>
                <th>Nível</th>
                <th>Categoria</th>
                <th>Ativo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="6" className="ap-empty">Nenhuma palavra encontrada.</td></tr>
              ) : filtered.map(w => (
                <tr key={w.id}>
                  <td className="ap-td-word">{w.palavra}</td>
                  <td className="ap-td-ipa">{w.ipa || '—'}</td>
                  <td>
                    <span className={`ap-nivel ${w.nivel}`}>{NIVEL_LABELS[w.nivel]}</span>
                  </td>
                  <td>{w.categoria}</td>
                  <td>
                    <span className={`ap-status ${w.ativo ? 'on' : 'off'}`}>
                      {w.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="ap-actions">
                    <button className="ap-btn-edit" onClick={() => openEdit(w)} title="Editar">
                      <i className="fas fa-edit" />
                    </button>
                    <button className="ap-btn-del" onClick={() => handleDelete(w.id, w.palavra)} title="Excluir">
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
